import { createContext, useContext, useMemo } from 'react';
import { FlowStore } from 'src/features/flows/contexts/FlowStore';
import { ReactChildren } from 'src/shared/types/util.types';

/**
 * FlowStoreContext provides the FlowStore instance to all child components
 * Each provider instance creates its own FlowStore, ensuring proper lifecycle management
 */
const FlowStoreContext = createContext<FlowStore | null>(null);

/**
 * Hook to access the FlowStore instance from context
 * Must be used within a FlowStoreProvider
 */
export function useFlowStore(): FlowStore {
  const store = useContext(FlowStoreContext);
  if (!store) {
    throw new Error('useFlowStore must be used within a FlowStoreProvider');
  }
  return store;
}

/**
 * FlowStoreProvider creates a new FlowStore instance and provides it via context
 *
 * On mount: Creates a new FlowStore instance
 * On unmount: The instance is garbage collected automatically
 *
 * This ensures that when navigating between flows or away from the flow page,
 * a fresh store instance is created and old instances are properly cleaned up.
 */
export function FlowStoreProvider({ children }: ReactChildren) {
  // Create a new FlowStore instance for this provider
  // This instance will be garbage collected when the provider unmounts
  const flowStore = useMemo(() => {
    return new FlowStore();
  }, []);

  return <FlowStoreContext.Provider value={flowStore}>{children}</FlowStoreContext.Provider>;
}
